import { PropsWithChildren, createContext, useContext } from 'react'

import { keyBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { useExpensables as useExpensablesQuery } from 'modules/expenses/hooks/useExpensable'
import { EXPENSABLE_CLASS } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'

interface ContextType {
  expensablesById: Record<number, Expensable>
  loading: boolean
}

interface Params {
  supplementsStore: SupplementsFormStore
}

const ExpensablesContext = createContext<ContextType>({ expensablesById: {}, loading: false })

export const ExpensablesProvider = ({ supplementsStore, children }: PropsWithChildren<Params>) => {
  const { company } = useCurrentSession()

  const ids = supplementsStore.forms
    .map(
      (form) => form.values.source_type?.includes(EXPENSABLE_CLASS) && Number(form.values.source_id)
    )
    .filter(Boolean)

  const { expensables, loading } = useExpensablesQuery({
    ids,
    companyId: company.id,
    includeGrouped: true,
    skip: !ids.length,
  })

  const context = useUnsafeMemo(
    () => ({ expensablesById: keyBy(expensables, 'id'), loading }),
    [expensables, loading]
  )

  return <ExpensablesContext.Provider value={context}>{children}</ExpensablesContext.Provider>
}

export const useExpensables = () => useContext(ExpensablesContext)
